import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SchemaResponse } from '../../core/service/schema.service';

@Component({
  selector: 'app-schema-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './schema-selection-modal.component.html',
  styleUrls: ['./schema-selection-modal.component.scss']
})
export class SchemaSelectionModalComponent implements OnInit {
  @Input() availableTemplates: SchemaResponse[] = [];
  @Input() show: boolean = false;
  
  @Output() selectionMade = new EventEmitter<{
    workflowType: 'semi' | 'manual',
    workspaceName?: string,
    selectedTemplate?: SchemaResponse
  }>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('templateCard') templateCard?: ElementRef;
  @ViewChild('schemaPreview') schemaPreview?: ElementRef;
  @ViewChild('previewColumns') previewColumns?: ElementRef;

  selectedOption: 'ai' | 'manual' | 'template' = 'manual';
  selectedTemplateId: string | null = null;
  workspaceName: string = '';
  previewSheets: PreviewSheet[] = [];
  showPreview: boolean = false;
  activePreviewTab: number = 0;

  ngOnInit(): void {
    this.selectedOption = 'manual';
  }

  onOptionSelect(option: 'ai' | 'manual' | 'template') {
    this.selectedOption = option;
    if (option !== 'template') {
      this.selectedTemplateId = null;
      this.showPreview = false;
      this.previewSheets = [];
      this.activePreviewTab = 0;
    } else {
      setTimeout(() => {
        this.scrollToTemplateCard();
      }, 100);
    }
  }

  onTemplateSelect(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedTemplateId = select.value;
    
    if (this.selectedTemplateId) {
      const template = this.availableTemplates.find(t => t.schema_id === this.selectedTemplateId);
      if (template) {
        this.previewSheets = this.extractSchemaStructure(template);
        this.showPreview = this.previewSheets.length > 0;
        this.activePreviewTab = 0;
        
        if (this.showPreview) {
          setTimeout(() => {
            this.scrollToPreview();
          }, 0);
          setTimeout(() => {
            this.scrollToPreview();
          }, 300);
        }
      }
    } else {
      this.showPreview = false;
      this.previewSheets = [];
      this.activePreviewTab = 0;
    }
  }

  onPreviewClick(event: Event) {
    event.stopPropagation();
  }

  onPreviewTabClick(tabIndex: number, event: Event) {
    event.stopPropagation();
    this.activePreviewTab = tabIndex;
    
    if (this.previewColumns?.nativeElement) {
      this.previewColumns.nativeElement.scrollTop = 0;
    }
  }

  private extractSchemaStructure(template: SchemaResponse): PreviewSheet[] {
    const sheets: PreviewSheet[] = [];
    
    try {
      const schemaContent = template.schema_content;
      
      if (schemaContent.properties?.sheets?.items) {
        schemaContent.properties.sheets.items.forEach((sheetDef: any, index: number) => {
          const sheetName = sheetDef.properties?.sheet_name?.const || `Sheet ${index + 1}`;
          
          let defName = sheetName.replace(/\s+/g, '');
          
          if (!schemaContent.$defs[defName]) {
            defName = `Sheet_${index + 1}`;
          }
          
          const columns: PreviewColumn[] = [];
          if (schemaContent.$defs[defName]?.properties) {
            const properties = schemaContent.$defs[defName].properties;
            Object.keys(properties).forEach(columnName => {
              const columnDef = properties[columnName];
              columns.push({
                name: columnName,
                description: columnDef.description || '',
                example: columnDef.examples ? columnDef.examples[0] : ''
              });
            });
          }
          
          sheets.push({
            name: sheetName,
            columns: columns
          });
        });
      } else if (schemaContent.$defs) {
        Object.keys(schemaContent.$defs).forEach((defKey, index) => {
          const def = schemaContent.$defs[defKey];
          const columns: PreviewColumn[] = [];
          
          if (def.properties) {
            Object.keys(def.properties).forEach(columnName => {
              const columnDef = def.properties[columnName];
              columns.push({
                name: columnName,
                description: columnDef.description || '',
                example: columnDef.examples ? columnDef.examples[0] : ''
              });
            });
          }
          
          sheets.push({
            name: defKey,
            columns: columns
          });
        });
      }
    } catch (error) {
      console.error('Error extracting schema structure:', error);
    }
    
    return sheets;
  }

  onContinue() {
    const workspaceName = this.workspaceName.trim() || undefined;
    
    if (this.selectedOption === 'template') {
      if (!this.selectedTemplateId) {
        return; 
      }
      const template = this.availableTemplates.find(t => t.schema_id === this.selectedTemplateId);
      this.selectionMade.emit({
        workflowType: 'manual',
        workspaceName: workspaceName,
        selectedTemplate: template
      });
    } else if (this.selectedOption === 'ai') {
      this.selectionMade.emit({
        workflowType: 'semi',
        workspaceName: workspaceName
      });
    } else {
      this.selectionMade.emit({
        workflowType: 'manual',
        workspaceName: workspaceName
      });
    }
  }

  onCancel() {
    this.cancelled.emit();
  }

  private scrollToTemplateCard() {
    if (this.templateCard?.nativeElement) {
      this.templateCard.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }

  private scrollToPreview() {
    if (this.schemaPreview?.nativeElement) {
      const previewElement = this.schemaPreview.nativeElement;
      const modalBody = previewElement.closest('.modal-body');
      
      if (modalBody) {
        const previewRect = previewElement.getBoundingClientRect();
        const modalBodyRect = modalBody.getBoundingClientRect();
        const previewTop = previewRect.top - modalBodyRect.top + (modalBody as HTMLElement).scrollTop;
        const previewHeight = previewElement.offsetHeight;
        const modalBodyHeight = (modalBody as HTMLElement).clientHeight;

        const targetBottomAlignedScrollTop = previewTop + previewHeight - modalBodyHeight + 200;

        (modalBody as HTMLElement).scrollTo({
          top: Math.max(0, targetBottomAlignedScrollTop),
          behavior: 'smooth'
        });
      } else {
        previewElement.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }
  }
}

interface PreviewSheet {
  name: string;
  columns: PreviewColumn[];
}

interface PreviewColumn {
  name: string;
  description: string;
  example: string;
}

